/*!
 * Price Point AI Assistant — client-side "Liquid UI" chat widget.
 * No API key ever touches this file. The scripted engine (intent matching,
 * live product search, on-device translation) needs no network call at all.
 * The one exception is PROXY_ENDPOINT: an optional call to our own
 * Cloudflare Worker (see cloudflare-worker/worker.js), which holds the real
 * LLM API key server-side and is only used as a fallback for messages the
 * scripted engine can't confidently answer.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'pp_ai_widget_state_v1';
  var TEASER_SESSION_KEY = 'pp_ai_teaser_shown_v1';
  var QUOTA_RESET_KEY = 'pp_ai_quota_reset_at_v1';
  var IDLE_MS = 5000;
  var MAX_HISTORY = 40;
  var MAX_INPUT_LEN = 400;

  var ctx = window.PricePointContext || {};
  var GA_PARAM_MAX_LEN = 100; // GA4 custom parameter values are truncated/rejected past ~100 chars

  var PROXY_ENDPOINT = 'https://pp-ai-proxy.diegoserver-now.workers.dev/';

  // ---------------------------------------------------------------------
  // GA4 tracking — best-effort only. Analytics must never break the widget,
  // so every call is guarded and no-ops silently if gtag isn't present.
  // ---------------------------------------------------------------------
  function ppTruncate(str) {
    return String(str == null ? '' : str).slice(0, GA_PARAM_MAX_LEN);
  }

  function ppTrackEvent(eventName, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params || {});
      }
    } catch (e) {
      /* analytics failures must never break the widget */
    }
  }

  // ---------------------------------------------------------------------
  // Safe storage helpers (never let a corrupt/blocked storage break the page)
  // ---------------------------------------------------------------------
  function readStorage(key, storage) {
    try {
      var raw = (storage || window.localStorage).getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  function writeStorage(key, value, storage) {
    try {
      (storage || window.localStorage).setItem(key, JSON.stringify(value));
    } catch (e) {
      /* storage unavailable (private mode, quota, etc.) — fail silently */
    }
  }

  // ---------------------------------------------------------------------
  // Knowledge base — only facts explicitly true of Price Point. Anything
  // not covered here is answered honestly ("I don't have that specific
  // detail") rather than invented.
  // ---------------------------------------------------------------------
  // Last-resort URL fallbacks match Shopify's /pages/<handle> convention and
  // this theme's template names (page.sell-your-device.liquid) — used only
  // if the Liquid snippet failed to resolve the real page URLs.
  function ppPageFallback(handle) {
    var root = (ctx.rootUrl || '/').replace(/\/$/, '');
    return root + '/pages/' + handle;
  }
  var SOURCE_ABOUT = {
    label: 'Price Point — About Us',
    url: ctx.aboutUrl || ppPageFallback('about-us')
  };
  var SOURCE_SELL = {
    label: 'Price Point — Sell Your Device',
    url: ctx.sellDeviceUrl || ppPageFallback('sell-your-device')
  };

  // The LLM path can't attach a real citation link the way static intents
  // do — it just writes prose. If it happens to mention one of our known
  // pages by name, turn that into an actual clickable link instead of
  // leaving a dead mention (or worse, raw markdown asterisks) in the text.
  function detectKnownPageMention(text) {
    var norm = normalize(text);
    if (norm.indexOf('sell your device') !== -1 || norm.indexOf('sell my device') !== -1 || norm.indexOf('sell your phone') !== -1) {
      return SOURCE_SELL;
    }
    if (norm.indexOf('about us') !== -1 || norm.indexOf('about price point') !== -1) {
      return SOURCE_ABOUT;
    }
    return null;
  }

  var OFF_TOPIC_KEYWORDS = [
    'weather', 'politics', 'president', 'election', 'joke', 'recipe', 'cook',
    'homework', 'math', 'sports', 'football', 'basketball', 'movie', 'celebrity',
    'stock market', 'crypto', 'bitcoin', 'love', 'dating', 'relationship',
    'medical', 'diagnose', 'prescription', 'legal advice', 'lawsuit'
  ];

  var INTENTS = [
    {
      id: 'greeting',
      keywords: ['hi', 'hello', 'hey', 'yo', 'sup', 'howdy', 'good morning', 'good afternoon'],
      response: 'Hey there! I\'m the Price Point Assistant. I can help with questions about our electronics, how we source inventory, our sustainability practices, or selling your device. What would you like to know?',
      chips: ['How do you source products?', 'Tell me about sustainability', 'Sell my device']
    },
    {
      id: 'how_are_you',
      keywords: ['how are you', 'how are things', 'how is it going', 'hows it going', 'whats up', 'what is up'],
      response: 'Doing great, thanks for asking! Ready to help with anything about Price Point — our electronics, sourcing, sustainability, or selling a device.',
      chips: ['How do you source products?', 'Sell my device']
    },
    {
      id: 'about_company',
      keywords: ['who are you', 'what is price point', 'about price point', 'about your company', 'tell me about yourself', 'company', 'santa rosa', 'founded', 'when did you start'],
      response: 'Price Point is a small business based in Santa Rosa, California, founded in 2022. We specialize in electronics and consumer goods sourced from overstock and returns, so you get quality tech at a fair price.',
      simple: 'We\'re Price Point — a small electronics reseller in Santa Rosa, CA, started in 2022.',
      source: SOURCE_ABOUT,
      chips: ['How do you source products?', 'What are your values?']
    },
    {
      id: 'sourcing',
      keywords: ['source', 'sourced', 'sourcing', 'overstock', 'returns', 'returned', 'refurbished', 'refurb', 'used', 'secondhand', 'second-hand', 'supplier', 'suppliers', 'where do you get', 'where does it come from'],
      response: 'We source our inventory from overstock and returned items rather than manufacturing new stock. That keeps prices lower for you while keeping perfectly usable electronics out of landfills — transparency about where a product came from is something we take seriously.',
      simple: 'We sell overstock and returned electronics instead of brand-new ones — cheaper for you, less waste overall.',
      source: SOURCE_ABOUT,
      chips: ['Tell me about sustainability', 'What are your values?']
    },
    {
      id: 'sustainability',
      keywords: ['sustainability', 'sustainable', 'eco', 'environment', 'environmental', 'green', 'packaging', 'recycle', 'recycled', 'waste'],
      response: 'Sustainability is core to how we operate. We reuse packaging whenever possible instead of buying new boxes for every order, which cuts down on waste. It\'s part of our broader commitment to transparency and doing right by both customers and the planet.',
      simple: 'We reuse boxes and packaging instead of buying new ones for every order.',
      source: SOURCE_ABOUT,
      chips: ['How do you source products?', 'Sell my device']
    },
    {
      id: 'sell_device',
      keywords: [
        'sell my device', 'sell my phone', 'sell a device', 'trade in', 'trade-in', 'how do i sell',
        'sell your device', 'sell my laptop', 'sell my tablet', 'im selling', 'i am selling', 'selling my',
        'not turning on', 'wont turn on', 'won t turn on', 'stopped working', 'not working',
        'broken screen', 'cracked screen', 'screen is cracked', 'screen is broken', 'screen cracked',
        'wont charge', 'won t charge', 'battery dead', 'dead battery', 'water damage', 'can i sell'
      ],
      response: 'We\'d love to take a look at your device! Head over to our Sell Your Device page — tell us a bit about the item, and our team will review it and follow up with next steps.',
      variants: [
        'We\'d love to take a look at your device! Head over to our Sell Your Device page — tell us a bit about the item, and our team will review it and follow up with next steps.',
        'Absolutely — we take devices like that! The Sell Your Device page is the place to start: describe the item there and our team will get back to you with next steps.',
        'Yes! Just fill in the details on our Sell Your Device page and we\'ll review it and follow up. It only takes a minute.'
      ],
      simple: 'Go to our Sell Your Device page, tell us about the item, and we\'ll follow up.',
      source: SOURCE_SELL,
      chips: ['What condition do devices need to be in?', 'About Price Point']
    },
    {
      id: 'condition',
      keywords: ['condition', 'grading', 'graded', 'like new', 'cosmetic', 'scratches', 'scratched', 'dents', 'how used', 'how worn'],
      response: 'Since our inventory comes from overstock and returns, condition varies item to item — we\'re upfront about it, and each product page notes the specific condition so you know exactly what you\'re getting before you buy.',
      simple: 'Condition varies by item since it\'s overstock/returns — check each product page for the specifics.',
      source: SOURCE_ABOUT,
      chips: ['How do you source products?', 'Sell my device']
    },
    {
      id: 'trust',
      keywords: ['scam', 'legit', 'legitimate', 'trustworthy', 'is this real', 'sketchy', 'safe to buy', 'is this a real company'],
      response: 'Totally fair question! We\'re a real small business in Santa Rosa, CA, operating since 2022. Transparency is one of our core values — we\'re upfront about where devices come from and their condition, precisely so you don\'t have to just take our word for it.',
      simple: 'We\'re a real, small Santa Rosa business (since 2022) — transparency is one of our core values.',
      source: SOURCE_ABOUT,
      chips: ['About Price Point', 'How do you source products?']
    },
    {
      id: 'comparison',
      keywords: ['why not amazon', 'why not best buy', 'compared to amazon', 'vs amazon', 'why should i buy from you', 'why buy from you', 'better than amazon'],
      response: 'Big retailers sell new stock at full markup. We focus on overstock and returned electronics instead — often the same quality tech, at a lower price, with less waste. Plus you\'re supporting an actual small business rather than a warehouse.',
      simple: 'We sell overstock/returns at lower prices instead of new-at-full-price like the big retailers.',
      source: SOURCE_ABOUT,
      chips: ['How do you source products?', 'Tell me about sustainability']
    },
    {
      id: 'shipping',
      keywords: ['shipping cost', 'free shipping', 'when will it arrive', 'how long to ship', 'shipping speed', 'shipping time', 'how long does shipping take'],
      response: 'Shipping details (cost, speed, and tracking) are shown at checkout for your specific order and address — I don\'t want to guess at numbers I\'m not sure of, but checkout will have the exact details before you pay.',
      chips: ['About Price Point', 'How do you source products?']
    },
    {
      // Deliberately separate from the "shipping" intent above — "is my
      // order/delivery SAFE" is a trust/reassurance question, not a
      // logistics question, and answering it with "check checkout for
      // cost and tracking" (the old shared keyword list matched "delivery"
      // for both) reads as a cold non-answer to someone who's actually
      // asking to be reassured.
      id: 'order_safety',
      keywords: [
        'safety', 'safely', 'secure', 'securely', 'security', 'protected', 'protection',
        'insured', 'insurance', 'is it safe', 'is this safe', 'is my order safe',
        'items safe', 'item safe', 'safe when shipped', 'safe during shipping',
        'shipped safely', 'arrive safely', 'damaged in transit', 'arrive damaged',
        'is shipping safe', 'is delivery safe'
      ],
      response: 'Totally fair to want to feel good about that. We take real care with every order, and transparency and customer satisfaction are the values we build everything around — this isn\'t a faceless warehouse, it\'s a small Santa Rosa business that wants you happy with what shows up. If anything about your order ever seems off, our team on the Contact page is easy to reach and wants to sort it out.',
      simple: 'We take care with every order and want you to feel good about it — reach out on the Contact page if anything\'s off.',
      source: SOURCE_ABOUT,
      chips: ['About Price Point', 'How do you source products?']
    },
    {
      id: 'payment',
      keywords: ['payment', 'pay', 'credit card', 'paypal', 'afterpay', 'klarna', 'checkout', 'payment methods'],
      response: 'Checkout runs through our standard secure payment system, with whichever payment options are available shown right there at checkout.',
      chips: ['About Price Point', 'How do you source products?']
    },
    {
      id: 'values',
      keywords: ['values', 'transparency', 'customer satisfaction', 'why trust you', 'trust', 'mission'],
      response: 'Everything we do comes back to three things: transparency, sustainability, and customer satisfaction. We want you to always know where your electronics came from and feel good about the purchase.',
      simple: 'We care about being upfront, eco-friendly, and making sure you\'re happy with your purchase.',
      source: SOURCE_ABOUT,
      chips: ['How do you source products?', 'Tell me about sustainability']
    },
    {
      id: 'contact_human',
      keywords: ['talk to a person', 'human', 'contact', 'support', 'email', 'phone number', 'customer service', 'representative'],
      response: 'I can help with general questions about Price Point, but for account-specific or order help, our team is the way to go — check the Contact page on our site for the best way to reach them.',
      chips: ['About Price Point']
    },
    {
      id: 'thanks',
      keywords: ['thanks', 'thank you', 'appreciate it', 'cool', 'awesome', 'great'],
      response: 'Anytime! Is there anything else you\'d like to know about Price Point or our electronics?',
      chips: ['How do you source products?', 'Sell my device']
    },
    {
      id: 'farewell',
      keywords: ['bye', 'goodbye', 'see ya', 'see you', 'later', 'have a good one'],
      response: 'Thanks for stopping by Price Point! Come back anytime you\'ve got questions.',
      chips: []
    },
    {
      id: 'returns_refunds',
      keywords: ['return', 'returns', 'refund', 'refunds', 'money back', 'dont like it', 'don\'t like it', 'send it back', 'return policy', 'can i return'],
      response: 'We want you happy with whatever you get from us — that\'s the whole "customer satisfaction" value in action. I don\'t have the exact return window or process memorized, so I don\'t want to guess at that, but our team on the Contact page can sort out a return or refund quickly and directly.',
      simple: 'Reach out on the Contact page for returns/refunds — our team handles those directly.',
      source: SOURCE_ABOUT,
      chips: ['Is my order safe?', 'About Price Point']
    },
    {
      id: 'warranty',
      keywords: ['warranty', 'guarantee', 'guaranteed', 'covered if it breaks', 'is it covered'],
      response: 'Good thing to check on. Since our stock comes from overstock and returns rather than new-in-box, coverage can vary item to item, so I\'d rather point you to the real answer than guess — the specific product page or our team on the Contact page can confirm exactly what\'s covered for that item.',
      simple: 'Coverage varies by item (overstock/returns) — check the product page or Contact page for specifics.',
      source: SOURCE_ABOUT,
      chips: ['What condition do devices need to be in?', 'Do you offer returns or refunds?']
    },
    {
      id: 'price_negotiation',
      keywords: ['lower the price', 'discount', 'price match', 'cheaper', 'best price', 'negotiate', 'can you do', 'any deals on this'],
      response: 'I can\'t personally adjust pricing myself, but since everything here is already overstock/returns priced to be competitive, it\'s usually a good deal versus buying new. If you\'ve got a specific item in mind, our team on the Contact page can tell you if there\'s any flexibility.',
      chips: ['How do you source products?', 'About Price Point']
    },
    {
      id: 'store_hours_location',
      keywords: ['open', 'store hours', 'business hours', 'can i visit', 'visit your store', 'your address', 'where are you located', 'physical store'],
      response: 'We\'re based in Santa Rosa, California. I don\'t want to give you an hour that might be outdated, so the Contact page will have the current, accurate hours and visiting info.',
      simple: 'Santa Rosa, CA — check the Contact page for current hours.',
      source: SOURCE_ABOUT,
      chips: ['About Price Point', 'How do you source products?']
    },
    {
      id: 'international_shipping',
      keywords: ['ship internationally', 'international shipping', 'ship to canada', 'ship outside the us', 'overseas shipping', 'ship worldwide'],
      response: 'For shipping outside the US, checkout will show you right away whether it\'s available for your address — it can depend on the specific item, so I\'d rather have you check there than guess.',
      chips: ['About Price Point']
    },
    {
      id: 'data_privacy',
      keywords: ['my data', 'privacy', 'sell my information', 'personal info', 'is my data safe', 'my information safe'],
      response: 'Good to ask. This chat itself doesn\'t send your messages anywhere beyond what\'s needed to answer you, and no personal data leaves your browser otherwise. For how the site handles account or checkout info specifically, our Privacy Policy page or the Contact page can confirm the details.',
      simple: 'This chat keeps your messages private — for site-wide data handling, check the Privacy Policy or Contact page.',
      chips: ['Is my order safe?', 'About Price Point']
    },
    {
      id: 'order_status_tracking',
      keywords: ['wheres my order', 'where is my order', 'track my order', 'order status', 'has my order shipped', 'my order hasnt arrived'],
      response: 'For a specific order, the tracking link from your confirmation email is the fastest source — I don\'t have access to individual order details myself, but our team on the Contact page can look it up directly if the email tracking isn\'t showing what you need.',
      chips: ['Is my order safe?', 'About Price Point']
    }
  ];

  // The tap-only menu shown when the AI backend is out of daily quota (see
  // enterGuidedMode) — a two-level drill-down (category, then its specific
  // questions) so a small rail/strip never has to show all 10 options
  // flatly. Every question label must be phrased so it actually matches its
  // intended intent's keywords above — these are tapped, not typed, so
  // there's no fuzzy/typo tolerance needed, just an exact intent hit.
  var GUIDED_MODE_CATEGORIES = [
    {
      label: 'About Us',
      questions: ['About Price Point', 'How do you source products?', 'Tell me about sustainability']
    },
    {
      label: 'Selling',
      questions: ['Sell my device', 'What condition do devices need to be in?']
    },
    {
      label: 'Orders',
      questions: ['Is my order safe?', 'Do you offer returns or refunds?', 'Is there a warranty?']
    },
    {
      label: 'Payments',
      questions: ['Payment methods']
    },
    {
      label: 'Support',
      questions: ['Talk to a person']
    }
  ];

  // Product-interest categories map a conversational phrase to real
  // storefront search terms. searchTerms is an ORDERED list of attempts:
  // Shopify's predictive search is prefix-based ("phone" does NOT match
  // "iPhone"), so a single generic term can miss obvious inventory — the
  // widget tries each term until one returns relevant results.
  // searchTerms cast a wide net across brands rather than relying on one —
  // this is the client-side fallback used only if the AI proxy is
  // unreachable (see fallbackToStatic), but it has to obey the same rule as
  // the primary path: a generic "phone" request must not silently resolve
  // to just one brand (e.g. iPhone) and hide Samsung/Pixel/Poco results.
  // sweepTerms() below fetches every term and merges results instead of
  // stopping at the first brand that returns a hit.
  var PRODUCT_CATEGORIES = [
    {
      id: 'phones', searchTerms: ['phone', 'smartphone', 'iphone', 'samsung galaxy', 'google pixel', 'poco', 'oneplus'],
      keywords: ['phone', 'phones', 'iphone', 'iphones', 'smartphone', 'smartphones', 'android phone', 'galaxy phone', 'pixel phone', 'cellphone', 'cellphones', 'cell phone', 'cell phones', 'mobile phone', 'mobile phones', 'mobiles']
    },
    {
      id: 'laptops', searchTerms: ['laptop', 'macbook', 'notebook', 'chromebook'],
      keywords: ['laptop', 'laptops', 'macbook', 'macbooks', 'notebook', 'notebooks', 'chromebook', 'chromebooks', 'laptop computer', 'notebook computer']
    },
    { id: 'tablets', searchTerms: ['ipad', 'galaxy tab', 'android tablet'], keywords: ['tablet', 'tablets', 'ipad', 'ipads'] },
    { id: 'tvs', searchTerms: ['tv', 'television'], keywords: ['tv', 'tvs', 'television', 'televisions', 'smart tv', 'flatscreen', 'flat screen'] },
    {
      id: 'audio', searchTerms: ['headphones', 'bluetooth speaker', 'earbuds', 'soundbar'],
      keywords: ['headphone', 'headphones', 'earbud', 'earbuds', 'earphone', 'earphones', 'airpods', 'speaker', 'speakers', 'soundbar', 'bluetooth speaker']
    },
    { id: 'cameras', searchTerms: ['digital camera', 'gopro', 'canon camera'], keywords: ['camera', 'cameras', 'dslr', 'gopro', 'digital camera', 'video camera'] },
    {
      id: 'gaming', searchTerms: ['console', 'xbox', 'playstation', 'nintendo'],
      keywords: ['console', 'consoles', 'xbox', 'playstation', 'ps5', 'ps4', 'nintendo switch', 'gaming pc', 'game console', 'video game console']
    },
    {
      id: 'wearables', searchTerms: ['smartwatch', 'apple watch', 'fitbit'],
      keywords: ['watch', 'watches', 'smartwatch', 'smartwatches', 'apple watch', 'fitness tracker', 'fitbit', 'smart band']
    },
    {
      id: 'general_products', searchTerms: [''],
      keywords: ['recommend', 'recommendation', 'recommendations', 'suggest', 'suggestion', 'what do you have', 'what products', 'what do you sell', 'show me', 'looking for', 'any deals', 'deals on', 'browse products', 'inventory', 'in stock', 'what electronics', 'featured products', 'gadget', 'gadgets', 'want a gadget', 'electronics']
    }
  ];

  // Same "part/accessory, not the product itself" concept as the server's
  // product-taxonomy.js (kept as a separate, smaller copy here because this
  // file runs as a plain browser script, not an ES module the Worker can
  // import). Deliberately excludes ambiguous single words like "ram"/"ssd"
  // that also show up as spec call-outs on a device's own listing.
  var ACCESSORY_HINTS = [
    'case', 'cover', 'protector', 'screen protector', 'tempered glass', 'charger',
    'charging cable', 'cable', 'adapter', 'mount', 'stand', 'strap', 'band',
    'lavalier', 'microphone', 'mic', 'holder', 'sleeve', 'skin', 'dock', 'clip',
    'pouch', 'bumper', 'grip', 'car mount', 'tripod', 'part', 'parts',
    'replacement', 'battery pack', 'spare', 'accessory', 'accessories',
    'license', 'licenses', 'subscription',
    'sodimm', 'dimm', 'ddr2', 'ddr3', 'ddr4', 'ddr5', 'ram module', 'ram stick',
    'memory stick', 'memory upgrade', 'memory module', 'memory card', 'sd card',
    'lens cap', 'lens filter', 'lens hood', 'lens adapter', 'replacement lens', 'lens cover'
  ];

  function isAccessoryTitle(title) {
    var text = (title || '').toLowerCase();
    return ACCESSORY_HINTS.some(function (hint) {
      var escaped = hint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var boundary = new RegExp('(^|[^a-z0-9])' + escaped + '([^a-z0-9]|$)', 'i');
      return boundary.test(text);
    });
  }

  function singularize(word) {
    return (word.length > 4 && word.charAt(word.length - 1) === 's') ? word.slice(0, -1) : word;
  }

  // Which of the category's keywords did the visitor actually say? Their own
  // word is the best first search attempt ("iphone" finds iPhones where the
  // generic "phone" can't, because Shopify search is prefix-based).
  function findMatchedCategoryKeyword(tokens, category) {
    var singles = category.keywords.filter(function (kw) { return kw.indexOf(' ') === -1; });
    var i, k;
    // Exact matches first — "iphone" must resolve to "iphone", not to
    // "phone" via a shorter fuzzy neighbor that happens to come earlier.
    for (i = 0; i < tokens.length; i++) {
      for (k = 0; k < singles.length; k++) {
        if (tokens[i] === singles[k]) return singularize(singles[k]);
      }
    }
    for (i = 0; i < tokens.length; i++) {
      for (k = 0; k < singles.length; k++) {
        if (fuzzyTokenMatches(tokens[i], singles[k])) return singularize(singles[k]);
      }
    }
    return null;
  }

  // Typo-tolerant matching: lets "sourcng", "sutainability", "iphonr" etc.
  // still hit the right intent, without a fuzzy library. Edit distance is
  // scaled to keyword length so short words (which would false-positive
  // easily) never get fuzzy-matched — only exact matches count for those.
  function levenshtein(a, b) {
    if (a === b) return 0;
    var al = a.length, bl = b.length;
    if (al === 0) return bl;
    if (bl === 0) return al;
    var prev = new Array(bl + 1);
    var curr = new Array(bl + 1);
    for (var j = 0; j <= bl; j++) prev[j] = j;
    for (var i = 1; i <= al; i++) {
      curr[0] = i;
      for (var k = 1; k <= bl; k++) {
        var cost = a[i - 1] === b[k - 1] ? 0 : 1;
        curr[k] = Math.min(prev[k] + 1, curr[k - 1] + 1, prev[k - 1] + cost);
      }
      var tmp = prev; prev = curr; curr = tmp;
    }
    return prev[bl];
  }

  function fuzzyTokenMatches(token, keyword) {
    if (keyword.length < 4) return false;
    var maxDist = keyword.length >= 7 ? 2 : 1;
    return levenshtein(token, keyword) <= maxDist;
  }

  // "I don't want a phone, show me laptops instead" shouldn't score the
  // phones category just because the word "phone" appears — a negation
  // word within a few tokens before a match flips it into a penalty
  // instead of a point, so the non-negated category wins the competition.
  var NEGATION_WORDS = ['not', 'no', 'dont', 'never', 'without', 'cant', 'isnt', 'wont', 'nope'];

  function isNegated(tokens, index) {
    var start = Math.max(0, index - 3);
    for (var i = start; i < index; i++) {
      if (NEGATION_WORDS.indexOf(tokens[i]) !== -1) return true;
    }
    return false;
  }

  function scoreKeywords(tokens, norm, keywords) {
    var score = 0;
    var multiWord = keywords.filter(function (kw) { return kw.indexOf(' ') !== -1; });
    var singleWord = keywords.filter(function (kw) { return kw.indexOf(' ') === -1; });

    multiWord.forEach(function (kw) {
      if (norm.indexOf(kw) !== -1) score += 3;
    });

    // Score per TOKEN, not per keyword: "iphone" is a near-neighbor of both
    // "phone" and "iphones" by edit distance, so scoring per-keyword let one
    // word rack up +2 three times (exact "iphone", fuzzy "phone", fuzzy
    // "iphones") and swamp every other intent. Each token can only ever
    // contribute once, however many related keywords it happens to resemble.
    tokens.forEach(function (token, idx) {
      var matched = singleWord.some(function (kw) {
        return token === kw || fuzzyTokenMatches(token, kw);
      });
      if (matched) {
        score += isNegated(tokens, idx) ? -3 : 2;
      }
    });
    return score;
  }

  // Lets the bot handle natural follow-ups without re-stating the topic
  // ("what about laptops instead", "show me more") and requests to
  // simplify the last answer ("I don't understand", "explain that simpler").
  var CONTINUATION_PHRASES = [
    'what about', 'how about', 'show me more', 'anything else', 'more options',
    'something else', 'other options', 'any other', 'more like this', 'cheaper', 'cheaper ones'
  ];
  var CONFUSION_PHRASES = [
    'dont understand', 'confused', 'what does that mean', 'simpler', 'explain again',
    'huh', 'not clear', 'too complicated', 'in simple terms', 'simplify', 'in simpler terms'
  ];
  // Breaks the sell_device sticky context back out to a normal product
  // search. Not just the literal word "buy" — "do you have a laptop" is
  // just as clearly a shopping question, not a description of a device
  // someone owns, even though it never says "buy".
  var BUY_SIGNAL_WORDS = [
    'buy', 'buying', 'purchase', 'purchasing', 'do you have', 'do you sell',
    'do you carry', 'in stock', 'is there', 'you got', 'got any', 'available'
  ];

  // Keyword lists can't enumerate every device/brand name someone might
  // mention ("sell my iPhone", "sell my Xbox", "sell my Samsung..."). This
  // catches the general "sell (my/a/the) <anything>" pattern so sell_device
  // wins regardless of which specific noun follows, even on the very first
  // message (before any sticky context exists).
  var SELL_PATTERN = /\bsell\w*\s+(my|a|an|the|this|that)?\s*\w+/;

  // "take me to the selling page", "can I see the about page" — an explicit
  // request to GO somewhere deserves a short confirmation + the Yes/No
  // navigation prompt, not the full canned explanation paragraph.
  var NAV_REQUEST_PATTERN = /\b(take me|bring me|show me|can i see|i want to see|go to|open|visit)\b/;

  var NAV_REPLIES = [
    'Sure — right this way!',
    'Of course, here you go:',
    'You got it — one tap away:'
  ];

  // Makes the sell_device reply actually acknowledge what was said instead
  // of repeating the same canned paragraph regardless of the details given —
  // still 100% deterministic/free, just less robotic.
  // Patterns are word-order agnostic ("cracked screen" AND "the screen is
  // cracked") and written against normalize()'d text, where apostrophes are
  // stripped ("won't" becomes "won t").
  var CONDITION_ACKS = [
    { pattern: /(crack|brok|shatter)\w*[^.]{0,15}screen|screen[^.]{0,20}(crack|brok|shatter)/, text: 'A cracked or broken screen is totally fine to mention — ' },
    { pattern: /not turning on|won?\s?t turn on|doesn?\s?t turn on|stopped working|not working/, text: 'No worries that it’s not powering on — ' },
    { pattern: /won?\s?t charge|battery[^.]{0,10}dead|dead[^.]{0,10}battery/, text: 'A dead battery or charging issue is no problem — ' },
    { pattern: /water damage|got wet|dropped[^.]{0,10}water/, text: 'Water damage is good to know upfront — ' }
  ];

  function applyConditionAck(result, norm) {
    if (result.topicId !== 'sell_device') return result;
    for (var i = 0; i < CONDITION_ACKS.length; i++) {
      if (CONDITION_ACKS[i].pattern.test(norm)) {
        var lowerFirst = result.text.charAt(0).toLowerCase() + result.text.slice(1);
        return {
          text: CONDITION_ACKS[i].text + lowerFirst,
          source: result.source,
          chips: result.chips,
          topicId: result.topicId,
          simple: result.simple
        };
      }
    }
    return result;
  }

  function containsAny(norm, phrases) {
    for (var i = 0; i < phrases.length; i++) {
      if (norm.indexOf(phrases[i]) !== -1) return true;
    }
    return false;
  }

  // Deliberately NOT "I can only help with X, Y, Z" — that's the exact
  // robotic refusal-loop phrasing we're avoiding. Redirect warmly, mention
  // something concrete, rotate phrasing so it doesn't feel canned.
  var OFF_TOPIC_RESPONSE = {
    text: 'Ha, that\'s outside my wheelhouse — I\'m here for Price Point stuff. Happy to talk through our electronics, how we source them, or help you sell a device though!',
    variants: [
      'Ha, that\'s outside my wheelhouse — I\'m here for Price Point stuff. Happy to talk through our electronics, how we source them, or help you sell a device though!',
      'That one\'s a bit outside what I can help with, but I\'m all yours for anything about Price Point — our gear, sourcing, or selling a device.',
      'Not really my department, sorry! Ask me anything about our electronics or how we source them and I\'m your person.'
    ],
    chips: ['About Price Point', 'How do you source products?', 'Sell my device'],
    topicId: 'off_topic'
  };

  var FALLBACK_RESPONSE = {
    text: 'Hmm, not totally sure I follow — mind rephrasing? Or pick one of these and I\'ll take it from there.',
    variants: [
      'Hmm, not totally sure I follow — mind rephrasing? Or pick one of these and I\'ll take it from there.',
      'I\'m not quite catching that one. Try saying it differently, or one of these might be what you\'re after.',
      'That didn\'t quite land for me — rephrase it, or tap one of these.'
    ],
    chips: ['About Price Point', 'How do you source products?', 'Tell me about sustainability', 'Sell my device'],
    topicId: 'fallback'
  };

  function normalize(str) {
    return String(str)
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function matchIntent(rawMessage) {
    var norm = normalize(rawMessage);
    if (!norm) return FALLBACK_RESPONSE;

    for (var i = 0; i < OFF_TOPIC_KEYWORDS.length; i++) {
      if (norm.indexOf(OFF_TOPIC_KEYWORDS[i]) !== -1) return OFF_TOPIC_RESPONSE;
    }

    var tokens = norm.split(' ');
    var bestScore = 0;
    var bestKind = null;
    var bestIntent = null;
    var bestCategory = null;

    INTENTS.forEach(function (intent) {
      var score = scoreKeywords(tokens, norm, intent.keywords);
      if (intent.id === 'sell_device' && SELL_PATTERN.test(norm)) {
        score += 4; // wins over a bare brand/product-name keyword match (e.g. "iphone" alone)
      }
      if (score > bestScore) {
        bestScore = score;
        bestKind = 'text';
        bestIntent = intent;
      }
    });

    // general_products is a vague catch-all ("show me what you have") that
    // can appear alongside a specific mention ("show me laptops") — it must
    // never outrank an actual category, so it's only considered afterward,
    // as a last resort if nothing more specific matched anything at all.
    PRODUCT_CATEGORIES.forEach(function (category) {
      if (category.id === 'general_products') return;
      var score = scoreKeywords(tokens, norm, category.keywords);
      if (score > bestScore) {
        bestScore = score;
        bestKind = 'product';
        bestCategory = category;
      }
    });

    if (bestScore < 2) {
      var general = null;
      for (var g = 0; g < PRODUCT_CATEGORIES.length; g++) {
        if (PRODUCT_CATEGORIES[g].id === 'general_products') { general = PRODUCT_CATEGORIES[g]; break; }
      }
      var generalScore = general ? scoreKeywords(tokens, norm, general.keywords) : 0;
      if (generalScore >= 2) {
        bestScore = generalScore;
        bestKind = 'product';
        bestCategory = general;
      }
    }

    if (bestScore < 2) return FALLBACK_RESPONSE;

    if (bestKind === 'text') {
      return { text: bestIntent.response, variants: bestIntent.variants, source: bestIntent.source, chips: bestIntent.chips, topicId: bestIntent.id, simple: bestIntent.simple };
    }

    // Try the visitor's own word first, then the category's defaults —
    // capped at 3 network attempts to bound worst-case latency.
    var attempts = [];
    var matchedKw = findMatchedCategoryKeyword(tokens, bestCategory);
    if (matchedKw) attempts.push(matchedKw);
    bestCategory.searchTerms.forEach(function (t) {
      if (attempts.indexOf(t) === -1) attempts.push(t);
    });
    attempts = attempts.slice(0, 5);

    return {
      type: 'product_search',
      searchTerm: attempts[0] || '',
      searchTerms: attempts,
      topicId: 'product_' + bestCategory.id,
      chips: ['Sell my device', 'How do you source products?']
    };
  }

  // ---------------------------------------------------------------------
  // Live product lookup — same-origin, same public JSON endpoints Shopify
  // themes already use for predictive search. No API key, no third party,
  // always reflects real current inventory instead of invented products.
  // ---------------------------------------------------------------------
  function ppRoot() {
    return (ctx.rootUrl || '/').replace(/\/$/, '');
  }

  function getImageUrl(p) {
    if (!p) return '';
    if (p.featured_image) {
      if (typeof p.featured_image === 'string') return p.featured_image;
      if (p.featured_image.url) return p.featured_image.url;
    }
    if (p.image && typeof p.image === 'string') return p.image;
    return '';
  }

  function formatDollarPrice(amount) {
    var num = parseFloat(amount);
    if (isNaN(num)) return '';
    return '$' + num.toFixed(2); // Price Point is USD-only; no multi-currency handling
  }

  function parseSuggestResponse(data) {
    var resources = data && data.resources ? (data.resources.results || data.resources) : {};
    var products = resources.products || [];
    return products.map(function (p) {
      return {
        title: p.title || '',
        url: p.url || '#',
        price: p.price || '',
        image: getImageUrl(p),
        type: p.product_type || ''
      };
    });
  }

  function parseProductsJsonResponse(data) {
    var products = (data && data.products) || [];
    var root = ppRoot();
    return products.map(function (p) {
      var variants = p.variants || [];
      var minPrice = null;
      variants.forEach(function (v) {
        var n = parseFloat(v.price);
        if (!isNaN(n) && (minPrice === null || n < minPrice)) minPrice = n;
      });
      return {
        title: p.title || '',
        url: root + '/products/' + p.handle,
        price: minPrice !== null ? formatDollarPrice(minPrice) : '',
        image: (p.images && p.images[0] && p.images[0].src) || '',
        type: p.product_type || ''
      };
    });
  }

  // Shopify's search can return results that only loosely match (e.g. a RAM
  // stick tagged "compatible with laptops" showing up for a "laptop"
  // search). Only trust a result as a real answer if the category word
  // actually appears in its title — otherwise treat it as "no real match"
  // and offer what was actually found as a distinct suggestion instead.
  function isTitleRelevant(title, searchTerm) {
    if (!searchTerm) return true;
    var titleNorm = normalize(title);
    var words = normalize(searchTerm).split(' ').filter(Boolean);
    return words.some(function (w) { return titleNorm.indexOf(w) !== -1; });
  }

  function deriveSuggestionTerm(product) {
    if (product.type) return product.type;
    return product.title.split(' ').slice(0, 3).join(' ');
  }

  function fetchProducts(term, signal) {
    var root = ppRoot();
    var endpoint = term
      ? root + '/search/suggest.json?q=' + encodeURIComponent(term) +
        '&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=hide' +
        // Restrict matching to title/product_type/tag instead of Shopify's default
        // full-text search (which also scans body copy — e.g. a RAM stick's
        // description mentioning "for laptop or desktop" would otherwise match
        // a "laptop" search even though it isn't one).
        '&resources[options][fields]=title,product_type,tag'
      : root + '/collections/all/products.json?limit=4';
    var parser = term ? parseSuggestResponse : parseProductsJsonResponse;

    return fetch(endpoint, { signal: signal, headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('pp_ai_bad_status');
        return res.json();
      })
      .then(parser)
      .catch(function (err) {
        if (err && err.name === 'AbortError') throw err;
        return []; // treat any network/parse failure as "no live results", not a crash
      });
  }

  // ---------------------------------------------------------------------
  // On-device translation (Chrome's built-in Translator/LanguageDetector
  // APIs) — the one legitimate way to get real multi-language understanding
  // with zero API key and zero network call. It runs entirely on-device, so
  // it only exists in browsers that ship it (Chrome, as of 2026); everywhere
  // else this quietly no-ops and the widget just runs in English, exactly
  // as before. Never required for the widget to function.
  // ---------------------------------------------------------------------
  var translatorCache = {};
  var languageDetectorPromise = null;
  var TRANSLATION_TIMEOUT_MS = 5000;

  // Chrome's on-device model can take a while the first time a language pair
  // is used (provisioning/downloading), and in some environments the call
  // can simply hang. Nothing here may ever block the chat — if this doesn't
  // resolve in time, we proceed in English for this turn rather than freeze.
  function ppWithTimeout(promise, ms, fallbackValue) {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        resolve(fallbackValue);
      }, ms);
      Promise.resolve(promise).then(
        function (v) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(v);
        },
        function () {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(fallbackValue);
        }
      );
    });
  }

  function ppTranslationSupported() {
    try {
      return typeof self.Translator !== 'undefined' && typeof self.LanguageDetector !== 'undefined';
    } catch (e) {
      return false;
    }
  }

  function getLanguageDetector() {
    if (!languageDetectorPromise) {
      languageDetectorPromise = LanguageDetector.create().catch(function () { return null; });
    }
    return languageDetectorPromise;
  }

  function getTranslator(sourceLanguage, targetLanguage) {
    var key = sourceLanguage + '->' + targetLanguage;
    if (!translatorCache[key]) {
      translatorCache[key] = Translator.availability({ sourceLanguage: sourceLanguage, targetLanguage: targetLanguage })
        .then(function (availability) {
          if (availability === 'unavailable') return null;
          return Translator.create({ sourceLanguage: sourceLanguage, targetLanguage: targetLanguage });
        })
        .catch(function () { return null; });
    }
    return translatorCache[key];
  }

  // Resolves to a BCP-47 language code — 'en' if undetectable, low-confidence,
  // unsupported in this browser, or the on-device model didn't respond in time.
  function detectLanguage(text) {
    if (!ppTranslationSupported()) return Promise.resolve('en');
    var work = getLanguageDetector().then(function (detector) {
      if (!detector) return 'en';
      return detector.detect(text)
        .then(function (results) {
          var top = results && results[0];
          if (top && top.detectedLanguage && top.detectedLanguage !== 'und' && top.confidence > 0.6) {
            return top.detectedLanguage;
          }
          return 'en';
        })
        .catch(function () { return 'en'; });
    });
    return ppWithTimeout(work, TRANSLATION_TIMEOUT_MS, 'en');
  }

  function translateText(text, sourceLanguage, targetLanguage) {
    if (!text || sourceLanguage === targetLanguage || !ppTranslationSupported()) {
      return Promise.resolve(text);
    }
    var work = getTranslator(sourceLanguage, targetLanguage).then(function (translator) {
      if (!translator) return text;
      return translator.translate(text).catch(function () { return text; });
    });
    return ppWithTimeout(work, TRANSLATION_TIMEOUT_MS, text);
  }

  // ---------------------------------------------------------------------
  // LLM streaming — the ONLY network call here is to our own Cloudflare
  // Worker proxy (see cloudflare-worker/worker.js), which forwards to
  // OpenRouter. The API key never touches the browser; the Worker holds it
  // as a server-side secret and streams the upstream SSE response straight
  // through unmodified (OpenAI-compatible chunk format).
  // ---------------------------------------------------------------------
  function streamAssistantReply(message, history, onToken, onDone, onError, onQuotaExhausted) {
    if (!PROXY_ENDPOINT) {
      onError(new Error('pp_ai_proxy_not_configured'));
      return function cancel() {};
    }

    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;

    fetch(PROXY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller ? controller.signal : undefined,
      body: JSON.stringify({ message: message, history: history, pageContext: getPageContextLabel() })
    })
      .then(function (res) {
        if (!res.ok || !res.body) throw new Error('pp_ai_proxy_bad_status');

        // The Worker signals "the whole AI backend is out of quota for
        // today" with a plain JSON 200, distinct from the normal SSE stream
        // — distinguishing this from a random technical hiccup lets the
        // widget switch into a tap-only "guided mode" instead of repeatedly
        // failing every visitor's message for the rest of the day.
        var contentType = res.headers.get('Content-Type') || '';
        if (contentType.indexOf('application/json') !== -1) {
          return res.json().then(function (data) {
            if (data && data.quotaExhausted && onQuotaExhausted) {
              onQuotaExhausted(data.resetAt);
              return;
            }
            throw new Error('pp_ai_proxy_bad_status');
          });
        }

        var reader = res.body.getReader();
        var decoder = new TextDecoder();
        var buffer = '';

        function pump() {
          return reader.read().then(function (result) {
            if (result.done) {
              onDone();
              return;
            }
            buffer += decoder.decode(result.value, { stream: true });
            var lines = buffer.split('\n');
            buffer = lines.pop(); // keep the (possibly incomplete) last line for next chunk

            lines.forEach(function (line) {
              line = line.trim();
              if (!line || line.indexOf('data:') !== 0) return;
              var payload = line.slice(5).trim();
              if (!payload || payload === '[DONE]') return;
              try {
                // OpenAI-compatible chunk shape (OpenRouter, OpenAI, etc.):
                // { choices: [{ delta: { content: "..." } }] }
                var json = JSON.parse(payload);
                var choice = json.choices && json.choices[0];
                var delta = choice && choice.delta;
                if (delta && delta.content) onToken(delta.content);
              } catch (e) {
                /* ignore a malformed/partial SSE chunk — more will arrive */
              }
            });

            return pump();
          });
        }

        return pump();
      })
      .catch(function (err) {
        onError(err);
      });

    return function cancel() {
      if (controller) controller.abort();
    };
  }

  // ---------------------------------------------------------------------
  // Lightweight spring physics (mass-spring-damper), replacing the need
  // for a heavier animation library to get Framer-Motion-style bounce.
  // ---------------------------------------------------------------------
  function springAnimate(opts) {
    var from = opts.from, to = opts.to;
    var stiffness = opts.stiffness || 260;
    var damping = opts.damping || 22;
    var mass = opts.mass || 1;
    var onUpdate = opts.onUpdate;
    var onComplete = opts.onComplete;

    var position = from;
    var velocity = 0;
    var rafId = null;
    var lastTime = null;

    function step(time) {
      if (lastTime === null) lastTime = time;
      var dt = Math.min((time - lastTime) / 1000, 1 / 30);
      lastTime = time;

      var springForce = -stiffness * (position - to);
      var dampingForce = -damping * velocity;
      var acceleration = (springForce + dampingForce) / mass;
      velocity += acceleration * dt;
      position += velocity * dt;

      onUpdate(position);

      if (Math.abs(to - position) > 0.001 || Math.abs(velocity) > 0.001) {
        rafId = requestAnimationFrame(step);
      } else {
        onUpdate(to);
        if (onComplete) onComplete();
      }
    }

    rafId = requestAnimationFrame(step);
    return function cancel() {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }

  // ---------------------------------------------------------------------
  // Widget
  // ---------------------------------------------------------------------
  // Detects the current page from its URL to pick a specific greeting.
  // Liquid-provided context (ctx.isSellPage/isHome) takes precedence when
  // present, since it's accurate even if a merchant renames a page slug —
  // but the function works standalone from location.pathname too.
  function getPageGreeting() {
    if (ctx.isSellPage) return 'Need help with our device selling process?';
    if (ctx.isHome) return 'Have questions about Price Point?';

    var path = window.location.pathname.toLowerCase();
    if (path.indexOf('sell-your-device') !== -1 || path.indexOf('sell-device') !== -1) {
      return 'Need help with our device selling process?';
    }
    if (path === '/' || /^\/[a-z]{2}\/?$/.test(path)) {
      return 'Have questions about Price Point?';
    }
    return 'Questions about Price Point? Chat with our assistant.';
  }

  // A short, LLM-readable description of where the visitor actually is —
  // sent to the Worker so the assistant has situational awareness it never
  // had before (e.g. not re-explaining how to reach a page they're already on).
  function getPageContextLabel() {
    if (ctx.isSellPage) return 'the Sell Your Device page';
    if (ctx.isHome) return 'the homepage';
    var path = window.location.pathname.toLowerCase();
    if (path.indexOf('sell-your-device') !== -1 || path.indexOf('sell-device') !== -1) return 'the Sell Your Device page';
    if (path.indexOf('/products/') !== -1) return 'a product page';
    if (path.indexOf('/collections/') !== -1) return 'a collection/browse page';
    return 'a general page on the site';
  }

  function initialGreeting() {
    var greeting = getPageGreeting();
    if (ctx.isSellPage) return greeting + ' Ask me anything about selling your device to Price Point.';
    if (ctx.isHome) return greeting + ' I can help with our products, sourcing, or sustainability practices.';
    return 'Hi! I\'m the Price Point Assistant — ask me about our electronics, sourcing, or sustainability practices.';
  }

  function teaserText() {
    return getPageGreeting();
  }

  function PPAiWidget(root) {
    this.root = root;
    this.state = readStorage(STORAGE_KEY) || { open: false, messages: [], unread: 0 };
    if (!this.state.messages.length) {
      this.state.messages.push({ sender: 'bot', text: initialGreeting(), ts: Date.now() });
    }
    this.cancelSpring = null;
    this.lastIntent = null; // most recent question asked, for lead-attribution on link clicks
    this.productFetchController = null;
    this.conversationHistory = []; // ephemeral per-page-load context sent to the LLM proxy, not persisted
    this.lastProductSearchTerm = null; // enables "what about laptops instead" style follow-ups
    this.lastSimplifiable = null; // last answer that has a shorter "simple" variant on hand
    this.lastTopicId = null; // sticky conversation context (e.g. mid "sell my device" flow)
    this.lastVariantShown = null; // avoids repeating the same response phrasing twice in a row
    this.currentReplyLanguage = 'en'; // detected on-device per turn; 'en' if unsupported/undetected
    this.guidedMode = false; // true once the AI backend reports its daily quota is exhausted
    this.build();
    this.renderMessages();
    this.applyOpenState(true);
    this.bindEvents();
    this.startIdleWatcher();

    // A previous visit (today, or another visitor on this device) may have
    // already hit the daily quota — pick up that state immediately instead
    // of wasting a guaranteed-to-fail request finding out again.
    var storedResetAt = readStorage(QUOTA_RESET_KEY);
    if (storedResetAt && Date.now() < storedResetAt) {
      this.enterGuidedMode(storedResetAt, false);
    } else if (storedResetAt) {
      writeStorage(QUOTA_RESET_KEY, null);
    }
  }

  PPAiWidget.prototype.persist = function () {
    var trimmed = this.state.messages.slice(-MAX_HISTORY);
    this.state.messages = trimmed;
    writeStorage(STORAGE_KEY, this.state);
  };

  PPAiWidget.prototype.build = function () {
    var root = this.root;
    root.innerHTML =
      '<div class="pp-ai-teaser" id="pp-ai-teaser">' +
        '<button class="pp-ai-teaser-close" id="pp-ai-teaser-close" aria-label="Dismiss">&times;</button>' +
        '<span id="pp-ai-teaser-text"></span>' +
      '</div>' +
      '<div class="pp-ai-panel" id="pp-ai-panel" role="dialog" aria-label="Price Point Assistant">' +
        '<div class="pp-ai-header">' +
          '<div class="pp-ai-avatar">PP</div>' +
          '<div class="pp-ai-header-text">' +
            '<div class="pp-ai-header-title">Price Point Assistant</div>' +
            '<div class="pp-ai-header-sub"><span class="pp-ai-dot"></span>Online</div>' +
          '</div>' +
          '<button class="pp-ai-header-clear" id="pp-ai-clear" type="button">Clear</button>' +
        '</div>' +
        '<div class="pp-ai-messages" id="pp-ai-messages"></div>' +
        '<div class="pp-ai-chips" id="pp-ai-chips"></div>' +
        '<div class="pp-ai-suggestion" id="pp-ai-suggestion" hidden></div>' +
        '<div class="pp-ai-action" id="pp-ai-action" hidden></div>' +
        '<div class="pp-ai-inputbar">' +
          '<input class="pp-ai-input" id="pp-ai-input" type="text" maxlength="' + MAX_INPUT_LEN + '" placeholder="Ask about Price Point..." autocomplete="off" />' +
          '<button class="pp-ai-send" id="pp-ai-send" type="button" aria-label="Send">' +
            '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4 20-7Z"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="pp-ai-footnote">Automated assistant &middot; no personal data leaves your browser</div>' +
      '</div>' +
      '<div class="pp-ai-guided-rail" id="pp-ai-guided-rail" hidden></div>' +
      '<div class="pp-ai-launcher-wrap" id="pp-ai-launcher-wrap">' +
        '<div class="pp-ai-goo-layer">' +
          '<div class="pp-ai-blob pp-ai-blob-main"></div>' +
          '<div class="pp-ai-blob pp-ai-blob-2"></div>' +
          '<div class="pp-ai-blob pp-ai-blob-3"></div>' +
        '</div>' +
        '<button class="pp-ai-launcher" id="pp-ai-launcher" type="button" aria-label="Open Price Point Assistant">' +
          '<svg class="pp-ai-icon-chat" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>' +
          '<svg class="pp-ai-icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="M6 6l12 12"/></svg>' +
        '</button>' +
        '<span class="pp-ai-unread-badge" id="pp-ai-badge" hidden></span>' +
      '</div>';

    document.getElementById('pp-ai-teaser-text').textContent = teaserText();

    this.$panel = document.getElementById('pp-ai-panel');
    this.$launcherWrap = document.getElementById('pp-ai-launcher-wrap');
    this.$launcher = document.getElementById('pp-ai-launcher');
    this.$messages = document.getElementById('pp-ai-messages');
    this.$chips = document.getElementById('pp-ai-chips');
    this.$guidedRail = document.getElementById('pp-ai-guided-rail');
    this.$suggestion = document.getElementById('pp-ai-suggestion');
    this.$action = document.getElementById('pp-ai-action');
    this.$input = document.getElementById('pp-ai-input');
    this.$send = document.getElementById('pp-ai-send');
    this.$clear = document.getElementById('pp-ai-clear');
    this.$badge = document.getElementById('pp-ai-badge');
    this.$teaser = document.getElementById('pp-ai-teaser');
    this.$teaserClose = document.getElementById('pp-ai-teaser-close');
  };

  PPAiWidget.prototype.bindEvents = function () {
    var self = this;

    this.$launcher.addEventListener('click', function () {
      self.toggleOpen();
    });

    this.$clear.addEventListener('click', function () {
      self.state.messages = [{ sender: 'bot', text: initialGreeting(), ts: Date.now() }];
      self.persist();
      self.renderMessages();
      self.renderChips([]);
    });

    this.$send.addEventListener('click', function () { self.sendCurrentInput(); });
    this.$input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') self.sendCurrentInput();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.state.open) self.setOpen(false);
    });

    this.$teaserClose.addEventListener('click', function (e) {
      e.stopPropagation();
      self.hideTeaser();
    });
    this.$teaser.addEventListener('click', function () {
      self.hideTeaser();
      self.setOpen(true);
    });

    // Delegated click tracking for product links inside chat bubbles (bot
    // replies are rebuilt on every render, so this can't be bound per-link).
    this.$messages.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('a') : null;
      if (!link || !self.$messages.contains(link)) return;

      var isProductLink = false;
      try {
        isProductLink = new URL(link.href, window.location.origin).pathname.indexOf('/products/') !== -1;
      } catch (err) {
        isProductLink = false;
      }
      if (!isProductLink) return;

      var titleEl = link.querySelector('.pp-ai-product-title');
      var linkText = titleEl ? titleEl.textContent : link.textContent;

      ppTrackEvent('ai_conversion_lead', {
        intent: ppTruncate(self.lastIntent || ''),
        link_url: ppTruncate(link.href),
        link_text: ppTruncate(linkText || '')
      });
    });
  };

  PPAiWidget.prototype.hideTeaser = function () {
    this.$teaser.classList.remove('pp-ai-visible');
    writeStorage(TEASER_SESSION_KEY, true, window.sessionStorage);
  };

  PPAiWidget.prototype.startIdleWatcher = function () {
    var self = this;
    var timer = null;

    function scheduleIdleCheck() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        if (!self.state.open && !readStorage(TEASER_SESSION_KEY, window.sessionStorage)) {
          self.$teaser.classList.add('pp-ai-visible');
          self.$launcherWrap.classList.add('pp-ai-idle-pulse');
          setTimeout(function () {
            self.$launcherWrap.classList.remove('pp-ai-idle-pulse');
          }, 1200);
        }
      }, IDLE_MS);
    }

    ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'].forEach(function (evt) {
      window.addEventListener(evt, scheduleIdleCheck, { passive: true });
    });
    scheduleIdleCheck();
  };

  PPAiWidget.prototype.toggleOpen = function () {
    this.setOpen(!this.state.open);
  };

  PPAiWidget.prototype.setOpen = function (open) {
    this.state.open = open;
    this.syncGuidedRailVisibility();
    this.persist();
    if (open) {
      this.hideTeaser();
      this.state.unread = 0;
      this.persist();
      this.updateBadge();
      var self = this;
      setTimeout(function () { self.$input.focus(); }, 250);
      ppTrackEvent('ai_chat_opened', {
        page_context: ctx.isSellPage ? 'sell_your_device' : (ctx.isHome ? 'home' : 'other')
      });
    }
    this.applyOpenState(false);
  };

  PPAiWidget.prototype.applyOpenState = function (isInit) {
    var open = this.state.open;
    this.root.classList.toggle('pp-ai-open', open);

    if (this.cancelSpring) this.cancelSpring();

    var panel = this.$panel;
    var from = open ? 0 : 1;
    var to = open ? 1 : 0;

    if (isInit && !open) {
      panel.style.opacity = 0;
      panel.style.transform = 'scale(0.85) translateY(16px)';
      panel.style.borderRadius = '28px';
      panel.style.pointerEvents = 'none';
      this.updateBadge();
      return;
    }

    panel.style.pointerEvents = open ? 'auto' : 'none';
    this.cancelSpring = springAnimate({
      from: from,
      to: to,
      stiffness: 320,
      damping: 26,
      onUpdate: function (v) {
        var scale = 0.85 + 0.15 * v;
        var translate = 16 * (1 - v);
        var radius = 28 - 12 * v;
        panel.style.opacity = v;
        panel.style.transform = 'scale(' + scale + ') translateY(' + translate + 'px)';
        panel.style.borderRadius = radius + 'px';
      }
    });

    this.updateBadge();
    if (open) this.scrollToBottom();
  };

  PPAiWidget.prototype.updateBadge = function () {
    var n = this.state.unread || 0;
    if (n > 0 && !this.state.open) {
      this.$badge.hidden = false;
      this.$badge.textContent = n > 9 ? '9+' : String(n);
    } else {
      this.$badge.hidden = true;
    }
  };

  PPAiWidget.prototype.scrollToBottom = function () {
    var el = this.$messages;
    requestAnimationFrame(function () { el.scrollTop = el.scrollHeight; });
  };

  PPAiWidget.prototype.renderMessages = function () {
    var self = this;
    this.$messages.innerHTML = '';
    this.state.messages.forEach(function (m) {
      self.appendMessageNode(m.sender, m.text, m.sourceLabel, m.sourceUrl, m.products);
    });
    this.scrollToBottom();
  };

  PPAiWidget.prototype.buildSourceElement = function (sourceLabel, sourceUrl) {
    var src = document.createElement('span');
    src.className = 'pp-ai-msg-source';
    if (sourceUrl) {
      var a = document.createElement('a');
      a.href = sourceUrl;
      a.textContent = 'Source: ' + sourceLabel;
      src.appendChild(a);
    } else {
      src.textContent = 'Source: ' + sourceLabel;
    }
    return src;
  };

  PPAiWidget.prototype.appendMessageNode = function (sender, text, sourceLabel, sourceUrl, products) {
    var wrap = document.createElement('div');
    wrap.className = 'pp-ai-msg ' + (sender === 'user' ? 'pp-ai-msg-user' : 'pp-ai-msg-bot');
    // textContent only — never innerHTML with dynamic/user-controlled text.
    wrap.textContent = text;

    if (sourceLabel) {
      wrap.appendChild(this.buildSourceElement(sourceLabel, sourceUrl));
    }

    if (products && products.length) {
      var list = document.createElement('div');
      list.className = 'pp-ai-products';
      products.forEach(function (p) {
        var card = document.createElement('a');
        card.className = 'pp-ai-product-card';
        card.href = p.url;

        if (p.image) {
          var img = document.createElement('img');
          img.className = 'pp-ai-product-img';
          img.src = p.image;
          img.alt = p.title;
          img.loading = 'lazy';
          card.appendChild(img);
        }

        var info = document.createElement('span');
        info.className = 'pp-ai-product-info';

        var title = document.createElement('span');
        title.className = 'pp-ai-product-title';
        title.textContent = p.title;
        info.appendChild(title);

        if (p.price) {
          var price = document.createElement('span');
          price.className = 'pp-ai-product-price';
          price.textContent = p.price;
          info.appendChild(price);
        }

        card.appendChild(info);
        list.appendChild(card);
      });
      wrap.appendChild(list);
    }

    this.$messages.appendChild(wrap);
  };

  PPAiWidget.prototype.renderChips = function (chips) {
    var self = this;
    this.$chips.innerHTML = '';
    (chips || []).forEach(function (label) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pp-ai-chip';
      btn.textContent = label;
      btn.addEventListener('click', function () {
        self.$input.value = label;
        self.sendCurrentInput();
      });
      self.$chips.appendChild(btn);
    });
  };

  // Switches the widget into a tap-only mode once the AI backend reports
  // its daily quota is exhausted (see streamAssistantReply's
  // onQuotaExhausted). No typed message can reach the LLM in this state —
  // every response comes from the same static engine that already backs
  // every other fallback path, so nobody gets the "I don't understand you"
  // experience for the rest of the day; they get a curated menu instead.
  // `announce` is false when this is silently restored on page load because
  // a previous visit already hit the limit today.
  PPAiWidget.prototype.enterGuidedMode = function (resetAt, announce) {
    this.guidedMode = true;
    this.guidedActiveCategory = null; // null = showing top-level categories, not drilled into one
    writeStorage(QUOTA_RESET_KEY, resetAt);
    if (this.$panel) this.$panel.classList.add('pp-ai-guided-mode');
    if (this.$input) {
      this.$input.value = '';
      this.$input.disabled = true;
    }

    if (announce) {
      this.pushBotMessage(
        'Our AI assistant is taking a quick breather right now (it\'ll be back to full smarts tomorrow) — but I can still help directly with any of these:'
      );
    }
    this.syncGuidedRailVisibility();
    this.renderGuidedMenu();
  };

  // The rail lives OUTSIDE .pp-ai-panel (a true sibling in the DOM) so it
  // can use position:fixed relative to the real viewport — nested inside
  // the panel, it would be trapped by the panel's own overflow:hidden and
  // the transform the open/close spring animation applies, which creates a
  // new containing block that breaks position:fixed for any descendant.
  // Visibility has to be tracked manually here since it's no longer part of
  // the panel's own open/closed DOM subtree.
  PPAiWidget.prototype.syncGuidedRailVisibility = function () {
    var rail = this.$guidedRail;
    if (!rail) return;
    var show = !!(this.guidedMode && this.state.open);
    if (show) {
      if (!rail.hidden && !this.railAnimTimer) return; // already settled visible
      this.animateRail(true);
    } else {
      if (rail.hidden) return;
      this.animateRail(false);
    }
  };

  // Liquid open/close for the rail: on close, the buttons converge on the
  // corner nearest the launcher while the gooey SVG filter (the same one
  // the launcher blobs use) makes them visually MERGE into a single droplet
  // that then fades out; opening plays the same thing in reverse, popping
  // each button out of the droplet with a spring. Offsets are computed from
  // live geometry so the exact same code works for the desktop column and
  // the mobile horizontal strip.
  PPAiWidget.prototype.animateRail = function (show) {
    var self = this;
    var rail = this.$guidedRail;
    var buttons = Array.prototype.slice.call(rail.children);
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.railAnimTimer) {
      clearTimeout(this.railAnimTimer);
      this.railAnimTimer = null;
    }

    function cleanup() {
      rail.classList.remove('pp-ai-rail-goo');
      rail.style.opacity = '';
      rail.style.transition = '';
      buttons.forEach(function (btn) {
        btn.style.transition = '';
        btn.style.transitionDelay = '';
        btn.style.transform = '';
      });
    }

    if (reduced || !buttons.length) {
      cleanup();
      rail.hidden = !show;
      return;
    }

    // Snap any half-finished previous animation back to natural layout
    // before measuring, so the convergence math is based on real positions.
    buttons.forEach(function (btn) {
      btn.style.transition = 'none';
      btn.style.transitionDelay = '';
      btn.style.transform = '';
    });
    rail.hidden = false;
    void rail.offsetWidth;

    var rect = rail.getBoundingClientRect();
    function mergeOffset(btn) {
      var b = btn.getBoundingClientRect();
      return {
        x: (rect.right - 30) - (b.left + b.width / 2),
        y: (rect.bottom - 18) - (b.top + b.height / 2)
      };
    }

    var STAGGER = 42;
    rail.classList.add('pp-ai-rail-goo');

    if (show) {
      rail.style.opacity = '1';
      buttons.forEach(function (btn) {
        var off = mergeOffset(btn);
        btn.style.transform = 'translate(' + off.x + 'px,' + off.y + 'px) scale(.2)';
      });
      void rail.offsetWidth;
      buttons.forEach(function (btn, i) {
        btn.style.transition = 'transform .55s cubic-bezier(.34,1.56,.64,1)';
        btn.style.transitionDelay = (i * STAGGER) + 'ms';
        btn.style.transform = '';
      });
      this.railAnimTimer = setTimeout(function () {
        self.railAnimTimer = null;
        cleanup();
      }, 560 + buttons.length * STAGGER);
    } else {
      // Two phases so the ending reads as liquid, not a pop:
      //   1. converge — the buttons dive one-by-one into a single droplet
      //      (the goo filter blends them as they overlap);
      //   2. absorb — the merged droplet itself shrinks smoothly to nothing
      //      while fading, like it's being swallowed by the launcher.
      // Ending phase 1 with hidden=true looked like the droplet abruptly
      // vanished mid-shape — that was the reported bug.
      var offs = buttons.map(mergeOffset);
      var convergeEnd = 430 + buttons.length * STAGGER;
      buttons.forEach(function (btn, i) {
        btn.style.transition = 'transform .43s cubic-bezier(.55,-.28,.45,1.02)';
        btn.style.transitionDelay = (i * STAGGER) + 'ms';
        btn.style.transform = 'translate(' + offs[i].x + 'px,' + offs[i].y + 'px) scale(.3)';
      });
      this.railAnimTimer = setTimeout(function () {
        buttons.forEach(function (btn, i) {
          btn.style.transition = 'transform .34s cubic-bezier(.5,0,.75,.35)';
          btn.style.transitionDelay = '0ms';
          btn.style.transform = 'translate(' + offs[i].x + 'px,' + offs[i].y + 'px) scale(.01)';
        });
        rail.style.transition = 'opacity .3s ease .08s';
        rail.style.opacity = '0';
        self.railAnimTimer = setTimeout(function () {
          self.railAnimTimer = null;
          rail.hidden = true;
          cleanup();
        }, 420);
      }, convergeEnd);
    }
  };

  // Renders the current level of the guided-mode drill-down into the
  // standalone rail element. Top level shows the five categories; tapping
  // one shows a "Back" plus that category's specific questions, which is
  // what actually sends a message. CSS repositions the same rail into a
  // compact inline strip on narrow viewports — see pp-ai-widget.css.
  PPAiWidget.prototype.renderGuidedMenu = function () {
    var self = this;
    if (!this.$guidedRail) return;
    this.$guidedRail.innerHTML = '';

    if (this.guidedActiveCategory) {
      var back = document.createElement('button');
      back.type = 'button';
      back.className = 'pp-ai-rail-btn pp-ai-rail-back';
      back.textContent = '← Back';
      back.addEventListener('click', function () {
        self.guidedActiveCategory = null;
        self.renderGuidedMenu();
      });
      this.$guidedRail.appendChild(back);

      this.guidedActiveCategory.questions.forEach(function (label) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pp-ai-rail-btn';
        btn.textContent = label;
        btn.addEventListener('click', function () {
          self.$input.value = label;
          self.sendCurrentInput();
        });
        self.$guidedRail.appendChild(btn);
      });
      return;
    }

    GUIDED_MODE_CATEGORIES.forEach(function (category) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pp-ai-rail-btn pp-ai-rail-category';
      btn.textContent = category.label;
      btn.addEventListener('click', function () {
        self.guidedActiveCategory = category;
        self.renderGuidedMenu();
      });
      self.$guidedRail.appendChild(btn);
    });
  };

  // A distinct, dismissible "Did you mean X?" pill above the input bar —
  // separate from the chat transcript so it reads as a live suggestion,
  // not another message. Always built from a real fetched product, never
  // an invented guess.
  PPAiWidget.prototype.showSuggestion = function (text, onClick) {
    var self = this;
    this.$suggestion.innerHTML = '';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pp-ai-suggestion-btn';
    btn.textContent = text;
    btn.addEventListener('click', function () {
      self.clearSuggestion();
      onClick();
    });

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'pp-ai-suggestion-close';
    closeBtn.setAttribute('aria-label', 'Dismiss suggestion');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      self.clearSuggestion();
    });

    this.$suggestion.appendChild(btn);
    this.$suggestion.appendChild(closeBtn);
    this.$suggestion.hidden = false;
  };

  PPAiWidget.prototype.clearSuggestion = function () {
    this.$suggestion.hidden = true;
    this.$suggestion.innerHTML = '';
  };

  // iOS-style action prompt: "Go to the Sell Your Device page?" [Yes] [No].
  // Yes navigates in the same tab — the conversation survives because state
  // lives in localStorage, so the chat follows the visitor to the new page.
  PPAiWidget.prototype.showActionPrompt = function (question, url) {
    var self = this;
    if (!url) return; // nothing to navigate to — never show a dead prompt
    this.$action.innerHTML = '';

    var label = document.createElement('div');
    label.className = 'pp-ai-action-label';
    label.textContent = question;

    var buttons = document.createElement('div');
    buttons.className = 'pp-ai-action-buttons';

    var yesBtn = document.createElement('button');
    yesBtn.type = 'button';
    yesBtn.className = 'pp-ai-action-yes';
    yesBtn.textContent = 'Yes, take me there';
    yesBtn.addEventListener('click', function () {
      ppTrackEvent('ai_action_accepted', {
        intent: ppTruncate(self.lastIntent || ''),
        link_url: ppTruncate(url)
      });
      window.location.href = url;
    });

    var noBtn = document.createElement('button');
    noBtn.type = 'button';
    noBtn.className = 'pp-ai-action-no';
    noBtn.textContent = 'No thanks';
    noBtn.addEventListener('click', function () {
      self.clearActionPrompt();
    });

    buttons.appendChild(yesBtn);
    buttons.appendChild(noBtn);
    this.$action.appendChild(label);
    this.$action.appendChild(buttons);
    this.$action.hidden = false;
    this.scrollToBottom();
  };

  PPAiWidget.prototype.clearActionPrompt = function () {
    this.$action.hidden = true;
    this.$action.innerHTML = '';
  };

  PPAiWidget.prototype.sendCurrentInput = function () {
    var raw = this.$input.value.trim();
    if (!raw) return;
    raw = raw.slice(0, MAX_INPUT_LEN);
    this.$input.value = '';
    this.lastIntent = raw;
    this.pushMessage('user', raw);
    this.renderChips([]);
    this.clearSuggestion();
    this.clearActionPrompt();

    // Kick off language detection immediately/synchronously, while this call
    // is still directly inside the click/keydown handler that triggered it —
    // Chrome's Translator API requires that "transient activation" context.
    this.resolveLanguageAndRespond(raw);
  };

  // Detects the message's language on-device (Chrome only; resolves to 'en'
  // instantly everywhere else) and translates it to English so the existing
  // matching engine — which only understands English keywords — can run
  // unmodified. The ORIGINAL text (any language) is preserved for the LLM,
  // which understands other languages natively and needs no translation.
  PPAiWidget.prototype.resolveLanguageAndRespond = function (raw) {
    var self = this;
    detectLanguage(raw).then(function (lang) {
      self.currentReplyLanguage = lang;
      if (lang === 'en') {
        self.continueWithEnglishText(raw, raw);
        return;
      }
      // Warm the reverse direction (en -> lang) now, while the transient
      // activation from the triggering click/keydown is still valid — by
      // the time pushBotMessage() needs it, a setTimeout typing-delay will
      // likely have already crossed a task boundary and invalidated it.
      getTranslator('en', lang);
      translateText(raw, lang, 'en').then(function (englishText) {
        self.continueWithEnglishText(raw, englishText || raw);
      });
    });
  };

  PPAiWidget.prototype.continueWithEnglishText = function (originalRaw, englishText) {
    // matchIntent() is pure/synchronous — classify now, at submission time,
    // even though the reply is shown after a cosmetic typing delay.
    var result = matchIntent(englishText);
    var norm = normalize(englishText);

    // Explicit navigation request ("take me to the selling page") — short
    // confirmation + Yes/No prompt beats re-explaining the whole process.
    if (NAV_REQUEST_PATTERN.test(norm)) {
      var navTarget = null;
      if (/sell/.test(norm)) navTarget = { source: SOURCE_SELL, name: 'Sell Your Device' };
      else if (/about/.test(norm)) navTarget = { source: SOURCE_ABOUT, name: 'About Us' };
      if (navTarget && navTarget.source.url) {
        result = {
          text: NAV_REPLIES[Math.floor(Math.random() * NAV_REPLIES.length)],
          topicId: 'navigate',
          chips: [],
          actionQuestion: 'Go to the ' + navTarget.name + ' page?',
          actionUrl: navTarget.source.url
        };
      }
    }

    if (this.lastTopicId === 'sell_device' && result.type === 'product_search' && !containsAny(norm, BUY_SIGNAL_WORDS)) {
      // Still mid a "sell my device" conversation — a device/model mention
      // here is elaboration on what they're selling ("it's an iPhone 17 Pro
      // Max"), not a request to buy one. Stay in context instead of
      // hijacking it into a product search.
      var sellIntent = null;
      for (var i = 0; i < INTENTS.length; i++) {
        if (INTENTS[i].id === 'sell_device') { sellIntent = INTENTS[i]; break; }
      }
      result = { text: sellIntent.response, source: sellIntent.source, chips: sellIntent.chips, topicId: sellIntent.id, simple: sellIntent.simple };
    }

    if (result.topicId === 'fallback' && this.lastProductSearchTerm && containsAny(norm, CONTINUATION_PHRASES)) {
      // "what about laptops instead", "show me more" — carry the last product
      // search forward instead of dead-ending on a generic fallback message.
      result = {
        type: 'product_search',
        searchTerm: this.lastProductSearchTerm,
        topicId: 'product_followup',
        chips: ['Sell my device', 'How do you source products?']
      };
    } else if (result.topicId === 'fallback' && this.lastSimplifiable && containsAny(norm, CONFUSION_PHRASES)) {
      // "I don't understand" / "explain that simpler" — re-answer the last
      // topic in plainer language instead of the generic fallback message.
      var prev = this.lastSimplifiable;
      result = { text: prev.simple, source: prev.source, chips: prev.chips, topicId: 'simplified' };
    }

    // Rotate between response phrasings so asking about the same topic
    // twice doesn't produce the identical paragraph twice — never repeats
    // the immediately-previous wording.
    if (result.variants && result.variants.length > 1) {
      // result may be a shared singleton (OFF_TOPIC_RESPONSE, FALLBACK_RESPONSE)
      // rather than a fresh object — never mutate it directly, or the chosen
      // variant would permanently overwrite the shared default for everyone.
      var self2 = this;
      var pool = result.variants.filter(function (v) { return v !== self2.lastVariantShown; });
      var chosenText = pool[Math.floor(Math.random() * pool.length)];
      this.lastVariantShown = chosenText;
      result = {
        text: chosenText,
        variants: result.variants,
        source: result.source,
        chips: result.chips,
        topicId: result.topicId,
        simple: result.simple,
        type: result.type,
        searchTerm: result.searchTerm,
        searchTerms: result.searchTerms,
        actionQuestion: result.actionQuestion,
        actionUrl: result.actionUrl
      };
    }

    result = applyConditionAck(result, norm);

    if (result.type === 'product_search') this.lastProductSearchTerm = result.searchTerm;
    if (result.simple) this.lastSimplifiable = result;
    if (result.topicId && result.topicId !== 'fallback' && result.topicId !== 'navigate') this.lastTopicId = result.topicId;

    ppTrackEvent('ai_question_asked', {
      intent: ppTruncate(originalRaw),
      matched_topic: result.topicId || 'unmatched'
    });

    // Routing priority. The LLM is the smartest component we have, so it
    // gets everything it's genuinely better at; the scripted engine keeps
    // only what it does better than the LLM:
    //   - product searches (live inventory cards beat prose every time)
    //   - off-topic guard (instant, and not model-dependent)
    //   - short/simple messages ("hi", "thanks", chip clicks — instant,
    //     canned answers are fine and save the free-tier rate limit)
    // Complex conversational messages ("my iphone is 17 pro max, the
    // problem is..." ) go to the LLM FIRST, because keyword matching on
    // multi-clause sentences is exactly where canned replies feel robotic.
    // Whatever the scripted engine matched becomes the fallback if the LLM
    // is down/slow — so nobody ever gets a worse answer than before.
    var isComplexMessage = norm.split(' ').length >= 9;

    // Product searches and sell_device matches are exactly the cases a
    // keyword match can't safely resolve on its own — "do you sell phones?"
    // (conversational) vs "I want to buy an iPhone" (a real search), or
    // "do you sell X" vs "I want to sell my X". The Brain (Worker + LLM)
    // decides intent first and, for genuine searches, tells accessories
    // apart from the real product — the local match becomes the fallback
    // if the LLM path is ever unreachable, never the primary decision-maker.
    var needsIntentAnalysis = result.type === 'product_search' || result.topicId === 'sell_device';

    // Quota exhausted for the day — never attempt the LLM again this
    // session, even for intents (like sell_device) that would normally go
    // straight there. Every guided-mode chip maps to a real static answer.
    if (this.guidedMode) {
      this.respondTo(result);
      return;
    }

    if (result.topicId === 'fallback') {
      // The LLM understands other languages natively — pass the ORIGINAL
      // text, not the English translation, so its reply comes back in kind.
      this.respondWithLLM(originalRaw, result);
    } else if (
      PROXY_ENDPOINT &&
      (needsIntentAnalysis || (isComplexMessage && result.topicId !== 'off_topic' && result.topicId !== 'navigate'))
    ) {
      this.respondWithLLM(originalRaw, result);
    } else {
      this.respondTo(result);
    }
  };

  PPAiWidget.prototype.persistMessageState = function (sender, text, sourceLabel, sourceUrl, products) {
    this.state.messages.push({
      sender: sender,
      text: text,
      sourceLabel: sourceLabel || null,
      sourceUrl: sourceUrl || null,
      products: products || null,
      ts: Date.now()
    });
    this.persist();
    if (sender === 'bot' && !this.state.open) {
      this.state.unread = (this.state.unread || 0) + 1;
      this.persist();
      this.updateBadge();
    }
  };

  PPAiWidget.prototype.pushMessage = function (sender, text, sourceLabel, sourceUrl, products) {
    this.persistMessageState(sender, text, sourceLabel, sourceUrl, products);
    this.appendMessageNode(sender, text, sourceLabel, sourceUrl, products);
    this.scrollToBottom();
  };

  // Scripted-engine replies are always generated in English; if the visitor
  // wrote in another language (detected on-device), translate the outgoing
  // text back before displaying it. LLM replies bypass this entirely — it
  // writes in the visitor's language natively, so translating its output
  // would be redundant (and risk mangling an already-correct answer).
  PPAiWidget.prototype.pushBotMessage = function (text, sourceLabel, sourceUrl, products) {
    var self = this;
    var lang = this.currentReplyLanguage || 'en';
    if (lang === 'en') {
      this.pushMessage('bot', text, sourceLabel, sourceUrl, products);
      return;
    }
    translateText(text, 'en', lang).then(function (translated) {
      self.pushMessage('bot', translated || text, sourceLabel, sourceUrl, products);
    });
  };

  PPAiWidget.prototype.setThinking = function (isThinking) {
    var sub = this.root.querySelector('.pp-ai-header-sub');
    if (!sub) return;
    var textNode = sub.lastChild;
    sub.classList.toggle('pp-ai-thinking', isThinking);
    if (textNode) textNode.textContent = isThinking ? 'Typing…' : 'Online';
  };

  PPAiWidget.prototype.createStreamingBubble = function () {
    var wrap = document.createElement('div');
    wrap.className = 'pp-ai-msg pp-ai-msg-bot pp-ai-streaming';
    this.$messages.appendChild(wrap);
    this.scrollToBottom();
    return wrap;
  };

  PPAiWidget.prototype.finalizeStreamedMessage = function (bubbleEl, text) {
    bubbleEl.classList.remove('pp-ai-streaming');
    var source = detectKnownPageMention(text);
    var sourceLabel = source ? source.label : null;
    var sourceUrl = source ? source.url : null;
    if (sourceLabel) {
      bubbleEl.appendChild(this.buildSourceElement(sourceLabel, sourceUrl));
    }
    if (sourceUrl) {
      var pageName = source === SOURCE_SELL ? 'Sell Your Device' : 'About Us';
      this.showActionPrompt('Go to the ' + pageName + ' page?', sourceUrl);
    }
    this.persistMessageState('bot', text, sourceLabel, sourceUrl, null);
  };

  // Dispatch for anything the scripted engine matched confidently (static
  // text intents, off-topic, simplified/continuation results). The 'fallback'
  // case is handled earlier, in continueWithEnglishText, which routes to
  // the LLM directly instead of ever constructing a result that reaches here.
  PPAiWidget.prototype.respondTo = function (result) {
    if (result.type === 'product_search') {
      this.respondWithProducts(result);
      return;
    }

    var self = this;
    var typingEl = document.createElement('div');
    typingEl.className = 'pp-ai-typing';
    typingEl.innerHTML = '<span></span><span></span><span></span>';
    this.$messages.appendChild(typingEl);
    this.scrollToBottom();

    var delay = 380 + Math.random() * 420;
    setTimeout(function () {
      typingEl.remove();
      var sourceLabel = result.source ? result.source.label : null;
      var sourceUrl = result.source ? result.source.url : null;
      self.pushBotMessage(result.text, sourceLabel, sourceUrl);
      // In guided mode, always return to the drill-down rail (staying on
      // whichever category is active, so a follow-up question is one tap
      // away) instead of the matched intent's own generic chips — those use
      // plain chip styling and would silently replace the rail/strip.
      if (self.guidedMode) {
        self.renderGuidedMenu();
      } else {
        self.renderChips(result.chips || []);
      }
      if (result.actionUrl) {
        self.showActionPrompt(result.actionQuestion, result.actionUrl);
      } else if (result.topicId === 'sell_device' && sourceUrl) {
        self.showActionPrompt('Go to the Sell Your Device page?', sourceUrl);
      } else if (result.topicId === 'about_company' && sourceUrl) {
        self.showActionPrompt('Go to the About Us page?', sourceUrl);
      }
    }, delay);
  };

  PPAiWidget.prototype.respondWithLLM = function (userText, fallbackResult) {
    var self = this;
    var typingEl = document.createElement('div');
    typingEl.className = 'pp-ai-typing';
    typingEl.innerHTML = '<span></span><span></span><span></span>';
    this.$messages.appendChild(typingEl);
    this.scrollToBottom();
    this.setThinking(true);

    var bubble = null;
    var fullText = '';
    var settled = false;
    var cancelStream = null;

    function removeTyping() {
      if (typingEl.parentNode) typingEl.remove();
    }

    function fallbackToStatic() {
      self.setThinking(false);
      removeTyping();
      if (bubble) { bubble.remove(); bubble = null; }
      if (fallbackResult.type === 'product_search') {
        // fallbackResult has no .text — it's a search descriptor, not a
        // canned reply. Fall back to the real deterministic product search.
        self.respondWithProducts(fallbackResult);
        return;
      }
      // Reaching this function at all means the AI backend failed, timed
      // out, or came back empty — the LLM never actually got to weigh in.
      // If the local keyword matcher also had no real topic ("fallback"),
      // showing its generic "I didn't understand you" text would wrongly
      // blame the customer's phrasing for what's actually a hiccup on our
      // end — exactly backwards when the goal is to make people feel taken
      // care of, not brushed off. Only genuinely-matched topics (which have
      // a real canned answer worth showing) skip this.
      if (fallbackResult.topicId === 'fallback') {
        self.pushBotMessage(
          'Sorry about that — I\'m having a little trouble on my end right now, not a problem with your question. Please try again in a moment, or tap one of these and I\'ll take it from there:'
        );
        self.renderChips(fallbackResult.chips && fallbackResult.chips.length
          ? fallbackResult.chips
          : ['About Price Point', 'How do you source products?', 'Sell my device']);
        return;
      }
      var sourceLabel = fallbackResult.source ? fallbackResult.source.label : null;
      var sourceUrl = fallbackResult.source ? fallbackResult.source.url : null;
      self.pushBotMessage(fallbackResult.text, sourceLabel, sourceUrl);
      self.renderChips(fallbackResult.chips || []);
    }

    function keepPartialAnswer() {
      self.setThinking(false);
      self.conversationHistory.push({ role: 'user', text: userText });
      self.conversationHistory.push({ role: 'assistant', text: fullText });
      self.conversationHistory = self.conversationHistory.slice(-12);
      self.finalizeStreamedMessage(bubble, fullText);
      // Keep offering next steps from whichever topic the scripted engine
      // matched, even though the LLM wrote the actual answer.
      self.renderChips(fallbackResult.chips || []);
    }

    // If the LLM hasn't produced a single token within this window, give up
    // and degrade to the local engine rather than leave the user waiting.
    var giveUpTimer = setTimeout(function () {
      if (settled) return;
      settled = true;
      if (cancelStream) cancelStream();
      fallbackToStatic();
    }, 9000);

    cancelStream = streamAssistantReply(userText, this.conversationHistory,
      function onToken(chunk) {
        if (settled) return;
        clearTimeout(giveUpTimer);
        if (!bubble) {
          removeTyping();
          bubble = self.createStreamingBubble();
        }
        fullText += chunk;
        bubble.textContent = fullText; // textContent only — model output is never trusted as HTML
        self.scrollToBottom();
      },
      function onDone() {
        if (settled) return;
        settled = true;
        clearTimeout(giveUpTimer);
        if (!bubble || !fullText.trim()) {
          fallbackToStatic();
          return;
        }
        keepPartialAnswer();
      },
      function onError() {
        if (settled) return;
        settled = true;
        clearTimeout(giveUpTimer);
        if (bubble && fullText.trim()) {
          keepPartialAnswer(); // keep whatever streamed before the error
        } else {
          fallbackToStatic();
        }
      },
      function onQuotaExhausted(resetAt) {
        if (settled) return;
        settled = true;
        clearTimeout(giveUpTimer);
        self.setThinking(false);
        removeTyping();
        if (bubble) { bubble.remove(); bubble = null; }
        self.enterGuidedMode(resetAt, true);
      }
    );
  };

  PPAiWidget.prototype.respondWithProducts = function (result) {
    var self = this;
    var typingEl = document.createElement('div');
    typingEl.className = 'pp-ai-typing';
    typingEl.innerHTML = '<span></span><span></span><span></span>';
    this.$messages.appendChild(typingEl);
    this.scrollToBottom();

    if (this.productFetchController) {
      this.productFetchController.abort();
    }
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    this.productFetchController = controller;

    var minDelay = new Promise(function (resolve) {
      setTimeout(resolve, 380 + Math.random() * 420);
    });

    // Fetch every search term in PARALLEL and merge relevant, non-accessory
    // results across all of them — never stop at the first brand that
    // returns a hit. Shopify search is prefix-based, so "phone" misses
    // "iPhone"; the visitor's own word plus category brand alternates cover
    // that, but stopping early would silently resolve a generic "phone"
    // request to whichever single brand happened to be in stock (e.g.
    // iPhone), hiding Samsung/Pixel/Poco — the exact bug this exists to fix.
    var terms = (result.searchTerms && result.searchTerms.length) ? result.searchTerms : [result.searchTerm || ''];
    var signal = controller && controller.signal;

    function sweepTerms() {
      return Promise.all(terms.map(function (term) {
        return fetchProducts(term, signal).then(function (products) {
          return { term: term, products: products || [] };
        });
      })).then(function (results) {
        var seenUrls = {};
        var relevant = [];
        var allProducts = [];
        results.forEach(function (r) {
          r.products.forEach(function (p) {
            if (seenUrls[p.url]) return;
            seenUrls[p.url] = true;
            allProducts.push(p);
            var matches = r.term ? isTitleRelevant(p.title, r.term) : true;
            if (matches && !isAccessoryTitle(p.title)) relevant.push(p);
          });
        });
        return { relevant: relevant, allProducts: allProducts };
      });
    }

    Promise.all([sweepTerms(), minDelay])
      .then(function (values) {
        var outcome = values[0];
        var relevant = outcome.relevant;
        var allProducts = outcome.allProducts;
        var workedTerm = result.searchTerm || '';
        typingEl.remove();
        self.clearSuggestion();

        if (relevant.length) {
          self.lastProductSearchTerm = workedTerm;
          var intro = workedTerm
            ? 'Here’s what we currently have for "' + workedTerm + '":'
            : 'Here are some things currently available at Price Point:';
          self.pushBotMessage(intro, null, null, relevant);
          var browseUrl = workedTerm
            ? ppRoot() + '/search?q=' + encodeURIComponent(workedTerm)
            : ppRoot() + '/collections/all';
          var browseQuestion = workedTerm
            ? 'See all "' + workedTerm + '" results on our site?'
            : 'Browse everything in our store?';
          self.showActionPrompt(browseQuestion, browseUrl);
        } else if (allProducts.length) {
          // Shopify found something, just nothing that's actually a match —
          // manage expectations honestly instead of presenting a mismatch as
          // a real answer. Offers the closest real result as a one-click
          // alternative — never a "notify me" promise we can't actually keep.
          var altTerm = deriveSuggestionTerm(allProducts[0]);
          self.pushBotMessage(
            'I’ve checked our current inventory, and while I\'m not seeing any ' + result.searchTerm +
            ' in stock right now, we do have some ' + altTerm + ' available.'
          );
          self.showSuggestion('Show me the ' + altTerm, function () {
            self.clearSuggestion();
            self.pushMessage('user', altTerm);
            self.lastIntent = altTerm;
            self.respondWithProducts({
              type: 'product_search',
              searchTerm: altTerm,
              topicId: 'product_suggested',
              chips: result.chips || []
            });
          });
        } else {
          var searchUrl = ppRoot() + '/search?q=' + encodeURIComponent(result.searchTerm || '');
          self.pushBotMessage(
            'I\'m coming up empty on that in our current inventory — I don\'t want to guess, so let\'s try another way: browse everything we\'ve got, search the site directly, or our team can double-check on the Contact page.',
            'Price Point — Search',
            searchUrl
          );
        }
        self.renderChips(result.chips || []);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return; // superseded by a newer request
        typingEl.remove();
        self.pushBotMessage('I couldn\'t pull up live inventory just now — you can browse everything we\'ve got using the search bar at the top of the page.');
        self.renderChips(result.chips || []);
      });
  };

  function init() {
    var root = document.getElementById('pp-ai-root');
    if (!root) return;
    new PPAiWidget(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
